type ChartOption = Record<string, unknown>;

function seriesList(option: ChartOption): Record<string, unknown>[] {
  const series = option.series;
  return Array.isArray(series) ? (series as Record<string, unknown>[]) : [];
}

function seriesTypes(option: ChartOption): string[] {
  return seriesList(option).map((s) => String(s.type || "").toLowerCase());
}

export function normalizeCalendarHeatmapOption(option: ChartOption): ChartOption {
  const normalized = { ...option } as Record<string, any>;
  const series = Array.isArray(normalized.series) ? normalized.series : [];
  const heatmapSeries = series.filter((s: any) => s?.type === "heatmap");
  const hasCalendarHeatmap = series.some(
    (s: any) => s?.type === "heatmap" && (s?.coordinateSystem === "calendar" || s?.calendarIndex != null)
  );
  const hasAnyHeatmap = heatmapSeries.length > 0;
  if (!hasCalendarHeatmap && !hasAnyHeatmap) return normalized;

  const firstSeries = series.find((s: any) => s?.type === "heatmap") || {};
  const data = Array.isArray(firstSeries?.data) ? firstSeries.data : [];
  const dateValues = data
    .map((row: any) => (Array.isArray(row) ? row[0] : undefined))
    .filter((v: any) => typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v));
  const firstDate = dateValues[0] || new Date().toISOString().slice(0, 10);
  const year = String(firstDate).slice(0, 4);
  const minDate = dateValues.length > 0 ? dateValues[0] : `${year}-01-01`;
  const maxDate = dateValues.length > 0 ? dateValues[dateValues.length - 1] : `${year}-12-31`;

  const normalizeDateLike = (v: string) => {
    const m = String(v || "").match(/^(\d{4}-\d{2}-\d{2})/);
    return m ? m[1] : null;
  };

  const normalizeNameMap = (value: any, kind: "day" | "month") => {
    if (Array.isArray(value)) return value;
    if (typeof value !== "string") return undefined;
    const key = value.toLowerCase();
    if (key === "en" || key === "cn") return key;
    if (key === "tr") {
      return kind === "day"
        ? ["Paz", "Pzt", "Sal", "Car", "Per", "Cum", "Cmt"]
        : ["Oca", "Sub", "Mar", "Nis", "May", "Haz", "Tem", "Agu", "Eyl", "Eki", "Kas", "Ara"];
    }
    return "en";
  };

  if (hasCalendarHeatmap) {
    // Cartesian axes/grid conflict with calendar coordinateSystem and cause misaligned borders.
    delete normalized.xAxis;
    delete normalized.yAxis;
    delete normalized.grid;

    const calendarObj = Array.isArray(normalized.calendar)
      ? normalized.calendar.map((c: any) => ({ ...c }))
      : [{ ...(normalized.calendar as Record<string, any>) }];
    normalized.calendar = calendarObj.map((cal: any) => {
      let safeRange = cal.range;
      if (Array.isArray(safeRange)) {
        const [start, end] = safeRange;
        const startNorm = typeof start === "string" ? normalizeDateLike(start) : null;
        const endNorm = typeof end === "string" ? normalizeDateLike(end) : null;
        safeRange = startNorm && endNorm ? [startNorm, endNorm] : [minDate, maxDate];
      } else if (typeof safeRange === "string" && safeRange.includes("/")) {
        const [start, end] = safeRange.split("/");
        const startNorm = normalizeDateLike(start);
        const endNorm = normalizeDateLike(end);
        safeRange = startNorm && endNorm ? [startNorm, endNorm] : [minDate, maxDate];
      } else if (typeof safeRange === "string" && /\s+to\s+/i.test(safeRange)) {
        const [start, end] = safeRange.split(/\s+to\s+/i);
        const startNorm = normalizeDateLike(start);
        const endNorm = normalizeDateLike(end);
        safeRange = startNorm && endNorm ? [startNorm, endNorm] : [minDate, maxDate];
      } else if (typeof safeRange === "string") {
        const single = normalizeDateLike(safeRange);
        safeRange = single ? single : /^\d{4}$/.test(safeRange) ? safeRange : year;
      } else {
        safeRange = year;
      }
      return {
        ...cal,
        top: cal.top ?? 72,
        left: cal.left ?? 50,
        right: cal.right ?? 24,
        bottom: cal.bottom ?? 56,
        cellSize: cal.cellSize ?? ["auto", 14],
        range: safeRange,
        splitLine: cal.splitLine ?? { show: true, lineStyle: { color: "#ddd", width: 1 } },
        yearLabel: {
          ...(cal.yearLabel ?? {}),
          show: cal?.yearLabel?.show ?? true,
          margin: cal?.yearLabel?.margin ?? 8,
        },
        dayLabel: {
          ...(cal.dayLabel ?? {}),
          firstDay: cal?.dayLabel?.firstDay ?? 1,
          nameMap: normalizeNameMap(cal?.dayLabel?.nameMap, "day") ?? cal?.dayLabel?.nameMap ?? "en",
        },
        monthLabel: {
          ...(cal.monthLabel ?? {}),
          nameMap: normalizeNameMap(cal?.monthLabel?.nameMap, "month") ?? cal?.monthLabel?.nameMap ?? "en",
        },
      };
    });

    const normalizeVisualMapEntry = (vm: Record<string, any>) => ({
      ...vm,
      seriesIndex: vm.seriesIndex ?? [0],
      orient: vm.orient ?? "horizontal",
      left: vm.left ?? "center",
      bottom: typeof vm.bottom === "string" && vm.bottom.endsWith("%") ? 8 : (vm.bottom ?? 8),
      calculable: vm.calculable ?? true,
    });
    if (Array.isArray(normalized.visualMap)) {
      normalized.visualMap = normalized.visualMap.map((vm: any) =>
        vm && typeof vm === "object" ? normalizeVisualMapEntry(vm) : vm
      );
    } else if (normalized.visualMap && typeof normalized.visualMap === "object") {
      normalized.visualMap = normalizeVisualMapEntry(normalized.visualMap);
    }
  }

  const valueList = heatmapSeries
    .flatMap((s: any) => (Array.isArray(s?.data) ? s.data : []))
    .map((d: any) => {
      if (Array.isArray(d)) return d.length >= 3 ? Number(d[2]) : Number(d[1]);
      if (d && typeof d === "object" && "value" in d) {
        const v = (d as any).value;
        if (Array.isArray(v)) return v.length >= 3 ? Number(v[2]) : Number(v[1]);
        return Number(v);
      }
      return NaN;
    })
    .filter((n: number) => Number.isFinite(n));

  const minVal = valueList.length > 0 ? Math.min(...valueList) : 0;
  const maxVal = valueList.length > 0 ? Math.max(...valueList) : 100;
  if (normalized.visualMap == null && hasAnyHeatmap) {
    normalized.visualMap = {
      min: minVal,
      max: maxVal,
      calculable: true,
      orient: "horizontal",
      left: "center",
      bottom: 10,
      inRange: { color: ["#e8f5e9", "#66bb6a", "#1b5e20"] },
    };
  }

  const hasCartesianHeatmap = heatmapSeries.some(
    (s: any) => s?.type === "heatmap" && !s?.coordinateSystem
  );
  if (hasCartesianHeatmap) {
    normalized.grid = { containLabel: true, ...(normalized.grid as Record<string, unknown>) };
    if (normalized.xAxis && !Array.isArray(normalized.xAxis)) {
      normalized.xAxis = {
        axisLabel: { rotate: 45, hideOverlap: true },
        ...(normalized.xAxis as Record<string, unknown>),
      };
    }
  }

  return normalized;
}

export function normalize3dOption(option: ChartOption): ChartOption {
  const types = seriesTypes(option);
  if (!types.some((t) => t.endsWith("3d") || t === "surface")) {
    return option;
  }
  const normalized = { ...option } as Record<string, unknown>;
  if (!normalized.grid3D) {
    normalized.grid3D = {
      boxWidth: 200,
      boxDepth: 80,
      boxHeight: 80,
      viewControl: { projection: "perspective", autoRotate: false },
    };
  }
  for (const axis of ["xAxis3D", "yAxis3D", "zAxis3D"]) {
    if (!normalized[axis]) {
      normalized[axis] = { type: "category" };
    }
  }
  return normalized;
}

export function normalizeRadarOption(option: ChartOption): ChartOption {
  if (!seriesTypes(option).includes("radar")) return option;
  const normalized = { ...option } as Record<string, any>;
  const series = seriesList(normalized);
  const first = series[0] as Record<string, any> | undefined;
  const values = Array.isArray(first?.data?.[0]?.value) ? first.data[0].value : [];
  if (!normalized.radar && values.length > 0) {
    normalized.radar = {
      indicator: values.map((_: unknown, i: number) => ({ name: `Dim ${i + 1}`, max: 100 })),
    };
  } else if (normalized.radar && !Array.isArray(normalized.radar)) {
    normalized.radar = [normalized.radar];
  }
  return normalized;
}

export function normalizeGaugeOption(option: ChartOption): ChartOption {
  if (!seriesTypes(option).includes("gauge")) return option;
  const normalized = { ...option } as Record<string, any>;
  normalized.series = seriesList(normalized).map((s) => {
    if (String(s.type).toLowerCase() !== "gauge") return s;
    return {
      min: 0,
      max: 100,
      ...s,
      detail: { formatter: "{value}", ...(s.detail as Record<string, unknown>) },
    };
  });
  return normalized;
}

export function normalizeSankeyGraphOption(option: ChartOption): ChartOption {
  const types = seriesTypes(option);
  if (!types.some((t) => t === "sankey" || t === "graph")) return option;
  const normalized = { ...option } as Record<string, any>;
  normalized.series = seriesList(normalized).map((s) => {
    const type = String(s.type).toLowerCase();
    if (type !== "sankey" && type !== "graph") return s;
    const links = Array.isArray(s.links) ? s.links : Array.isArray(s.edges) ? s.edges : [];
    const nodes = Array.isArray(s.nodes)
      ? s.nodes
      : Array.isArray(s.data)
        ? s.data
        : [];
    return {
      ...s,
      layout: s.layout ?? (type === "graph" ? "force" : undefined),
      data: nodes,
      nodes,
      links,
    };
  });
  return normalized;
}

/** Widget/chat aliases that are not valid ECharts series.type values. */
export function normalizeSeriesTypeAliases(option: ChartOption): ChartOption {
  const series = seriesList(option);
  if (series.length === 0) return option;

  let changed = false;
  const next = series.map((s) => {
    const raw = String(s.type || "");
    const t = raw.toLowerCase().replace(/[_-\s]/g, "");
    if (t === "doughnut" || t === "donut") {
      changed = true;
      return { ...s, type: "pie", radius: s.radius ?? ["40%", "70%"] };
    }
    if (t === "rosepie" || t === "nightingale") {
      changed = true;
      return { ...s, type: "pie", roseType: s.roseType ?? "area", radius: s.radius ?? ["20%", "65%"] };
    }
    if (t === "area") {
      changed = true;
      return { ...s, type: "line", areaStyle: s.areaStyle ?? {} };
    }
    if (t === "stackedbar") {
      changed = true;
      return { ...s, type: "bar", stack: s.stack ?? "total" };
    }
    if (t === "stackedline") {
      changed = true;
      return { ...s, type: "line", stack: s.stack ?? "total" };
    }
    if (t === "stackedarea") {
      changed = true;
      return { ...s, type: "line", stack: s.stack ?? "total", areaStyle: s.areaStyle ?? {} };
    }
    return s;
  });

  if (!changed) return option;
  const normalized = { ...option } as Record<string, unknown>;
  normalized.series = next;
  delete normalized.chartType;
  return normalized;
}

const WATERFALL_TRANSPARENT = {
  borderColor: "rgba(0,0,0,0)",
  color: "rgba(0,0,0,0)",
};

function compactChartType(value: unknown): string {
  return String(value || "")
    .toLowerCase()
    .replace(/[_-\s]/g, "");
}

function waterfallNumber(value: unknown): number {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (value != null && typeof value === "object" && !Array.isArray(value)) {
    const raw = (value as { value?: unknown }).value;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * ECharts has no `series.type: "waterfall"`. A raw waterfall series draws axes and
 * hides the bars. Rebuild it as a transparent cumulative stack plus a visible bar,
 * matching the widget renderer.
 */
export function normalizeWaterfallOption(option: ChartOption): ChartOption {
  const series = seriesList(option);
  if (series.length === 0) return option;

  const chartType = compactChartType((option as { chartType?: unknown }).chartType);
  const hasRawWaterfall = series.some((s) => compactChartType(s.type) === "waterfall");
  const stackedBars = series.filter(
    (s) => compactChartType(s.type) === "bar" && compactChartType(s.stack) === "waterfall"
  );
  if (!hasRawWaterfall && stackedBars.length >= 2) return option;
  if (!hasRawWaterfall && chartType !== "waterfall") return option;

  let changed = false;
  const next: Record<string, unknown>[] = [];
  for (const s of series) {
    const type = compactChartType(s.type);
    const convert =
      type === "waterfall" ||
      (chartType === "waterfall" &&
        (type === "bar" || type === "") &&
        compactChartType(s.stack) !== "waterfall" &&
        Array.isArray(s.data));
    if (!convert) {
      next.push(s);
      continue;
    }
    const numbers = (Array.isArray(s.data) ? s.data : []).map(waterfallNumber);
    const placeholder: number[] = [];
    const values: number[] = [];
    let cumulative = 0;
    for (const v of numbers) {
      placeholder.push(cumulative);
      values.push(v);
      cumulative += v;
    }
    changed = true;
    next.push({
      type: "bar",
      stack: "waterfall",
      name: "",
      silent: true,
      tooltip: { show: false },
      itemStyle: WATERFALL_TRANSPARENT,
      emphasis: { itemStyle: WATERFALL_TRANSPARENT },
      data: placeholder,
    });
    next.push({
      ...s,
      type: "bar",
      stack: "waterfall",
      name: s.name != null && String(s.name) !== "" ? s.name : "Waterfall",
      data: values,
    });
  }

  if (!changed) return option;
  const normalized = { ...option, series: next } as ChartOption;
  delete (normalized as { chartType?: unknown }).chartType;
  return normalized;
}

/**
 * Chat sometimes puts pie slices directly in `series` instead of
 * `series: [{ type: "pie", data: [{ name, value }] }]`.
 * Detect flat `{name,value}` / `{name,y}` / `{x,y}` entries with no series `type`/`data`.
 */
export function normalizeFlatNamedValueAsPieSeries(option: ChartOption): ChartOption {
  const series = seriesList(option);
  if (series.length === 0) return option;

  const isFlatDatum = (d: Record<string, unknown>) => {
    if (Array.isArray(d.data)) return false;
    if (d.type != null && String(d.type).trim() !== "") return false;
    const hasName = d.name != null || d.x != null;
    const hasVal =
      (d.value != null && d.value !== "") || (d.y != null && d.y !== "");
    return hasName && hasVal;
  };

  if (!series.every((s) => isXyPoint(s) && isFlatDatum(s))) return option;

  const hint = String(
    (option as { chartType?: unknown }).chartType ||
      (option as { type?: unknown }).type ||
      "pie"
  )
    .toLowerCase()
    .replace(/[_-\s]/g, "");
  const seriesType = hint === "funnel" ? "funnel" : "pie";

  const data = series.map((d) => ({
    name: d.name != null ? String(d.name) : d.x != null ? String(d.x) : "",
    value:
      d.value != null && d.value !== ""
        ? Number(d.value)
        : d.y != null && d.y !== ""
          ? Number(d.y)
          : 0,
  }));

  const next: ChartOption = {
    ...option,
    series: [
      {
        type: seriesType,
        ...(seriesType === "pie" ? { radius: "55%" } : {}),
        data,
      },
    ],
  };
  delete (next as { chartType?: unknown }).chartType;
  if (!next.tooltip) {
    next.tooltip = { trigger: "item" };
  }
  return next;
}

/** Highcharts-style pie/category point: `["Label", 123]`. */
function isNameValueTuple(value: unknown): value is [unknown, unknown] {
  if (!Array.isArray(value) || value.length < 2) return false;
  const [name, raw] = value;
  if (name == null || (typeof name !== "string" && typeof name !== "number")) return false;
  if (raw == null || raw === "") return false;
  const n = Number(raw);
  return Number.isFinite(n);
}

function hasExplicitAxes(option: ChartOption): boolean {
  return option.xAxis != null || option.yAxis != null;
}

/**
 * Chat pie payloads often use Highcharts tuples `["name", value]` without `type: "pie"`.
 * Convert those to ECharts `{ name, value }` and infer pie when axes are absent.
 */
export function normalizeTupleNamedValueSeries(option: ChartOption): ChartOption {
  const series = seriesList(option);
  if (series.length === 0) return option;

  let changed = false;
  const next = series.map((s) => {
    const data = Array.isArray(s.data) ? s.data : [];
    if (data.length === 0 || !data.every(isNameValueTuple)) return s;

    const rawType = String(s.type || "")
      .toLowerCase()
      .replace(/[_-\s]/g, "");
    const isPieLike =
      rawType === "pie" ||
      rawType === "doughnut" ||
      rawType === "donut" ||
      rawType === "rosepie" ||
      rawType === "nightingale" ||
      rawType === "funnel";
    // Untyped series with only name/value tuples and no axes → pie (chat history / LLM shape).
    const inferPie = !rawType && !hasExplicitAxes(option);
    if (!isPieLike && !inferPie) return s;

    changed = true;
    const seriesType = rawType === "funnel" ? "funnel" : "pie";
    return {
      ...s,
      type: seriesType,
      ...(seriesType === "pie" && s.radius == null ? { radius: "55%" } : {}),
      data: data.map((d) => ({
        name: String(d[0]),
        value: Number(d[1]),
      })),
    };
  });

  if (!changed) return option;
  const normalized = { ...option, series: next } as ChartOption;
  if (!normalized.tooltip) {
    normalized.tooltip = { trigger: "item" };
  }
  return normalized;
}

/**
 * Pie/funnel chat payloads often use `{x,y}` or Highcharts `{name,y}`
 * instead of ECharts `{name,value}`.
 */
export function normalizeNamedValueSeries(option: ChartOption): ChartOption {
  const series = seriesList(option);
  if (series.length === 0) return option;

  let changed = false;
  const next = series.map((s) => {
    const type = String(s.type || "").toLowerCase();
    if (type !== "pie" && type !== "funnel") return s;
    const data = Array.isArray(s.data) ? s.data : [];
    if (data.length === 0) return s;
    // Convert when points lack ECharts `value` but carry chat/Highcharts `y` (and/or `x`).
    const needsConvert = data.every(
      (d) =>
        isXyPoint(d) &&
        (d.value == null || d.value === "") &&
        (Object.prototype.hasOwnProperty.call(d, "x") || Object.prototype.hasOwnProperty.call(d, "y"))
    );
    if (!needsConvert) return s;
    changed = true;
    return {
      ...s,
      data: data.map((d: any) => ({
        name: d.name != null ? String(d.name) : d.x != null ? String(d.x) : "",
        value: d.y != null && d.y !== "" ? Number(d.y) : d.value != null ? Number(d.value) : 0,
      })),
    };
  });

  if (!changed) return option;
  return { ...option, series: next };
}

export function normalizeThemeRiverOption(option: ChartOption): ChartOption {
  if (!seriesTypes(option).includes("themeriver")) return option;
  const normalized = { ...option } as Record<string, any>;
  if (!normalized.singleAxis) {
    normalized.singleAxis = {
      type: "time",
      top: 50,
      bottom: 50,
      axisTick: {},
      axisLabel: {},
    };
  }
  if (!normalized.tooltip) {
    normalized.tooltip = { trigger: "axis", axisPointer: { type: "line" } };
  }
  return normalized;
}

export function normalizeParallelOption(option: ChartOption): ChartOption {
  if (!seriesTypes(option).includes("parallel")) return option;
  const normalized = { ...option } as Record<string, any>;
  if (!normalized.parallelAxis) {
    const first = seriesList(normalized)[0];
    const row = Array.isArray(first?.data) ? first.data[0] : null;
    const dimCount = Array.isArray(row) ? row.length : 3;
    normalized.parallelAxis = Array.from({ length: dimCount }, (_, i) => ({ dim: i, name: `Dim ${i + 1}` }));
  }
  if (!normalized.parallel) {
    normalized.parallel = { left: "5%", right: "13%", bottom: "10%", top: "20%" };
  }
  return normalized;
}

/** Boxplot / candlestick need cartesian axes even though they are "statistical". */
export function normalizeStatisticalCartesianOption(option: ChartOption): ChartOption {
  const types = seriesTypes(option);
  if (!types.some((t) => t === "boxplot" || t === "candlestick")) return option;
  const normalized = { ...option } as Record<string, any>;
  const first = seriesList(normalized).find((s) => {
    const t = String(s.type || "").toLowerCase();
    return t === "boxplot" || t === "candlestick";
  });
  const data = Array.isArray(first?.data) ? first!.data : [];
  if (!normalized.xAxis) {
    normalized.xAxis = {
      type: "category",
      data: data.map((_: unknown, i: number) => String(i)),
      boundaryGap: true,
    };
  }
  if (!normalized.yAxis) {
    normalized.yAxis = { type: "value" };
  }
  if (!normalized.tooltip) {
    normalized.tooltip = { trigger: types.includes("candlestick") ? "axis" : "item" };
  }
  if (!normalized.grid) {
    normalized.grid = { containLabel: true, left: 48, right: 24, top: 40, bottom: 48 };
  }
  return normalized;
}

/** Cartesian heatmap without axes → infer category indices from [x,y,value] cells. */
export function normalizeCartesianHeatmapAxes(option: ChartOption): ChartOption {
  if (!isCartesianHeatmapChart(option)) return option;
  const normalized = { ...option } as Record<string, any>;
  const heat = seriesList(normalized).find((s) => String(s.type).toLowerCase() === "heatmap" && !s.coordinateSystem);
  const data = Array.isArray(heat?.data) ? heat!.data : [];
  const xs = new Set<string>();
  const ys = new Set<string>();
  for (const cell of data) {
    if (!Array.isArray(cell) || cell.length < 2) continue;
    xs.add(String(cell[0]));
    ys.add(String(cell[1]));
  }
  if (!normalized.xAxis) {
    normalized.xAxis = {
      type: "category",
      data: [...xs],
      splitArea: { show: true },
    };
  }
  if (!normalized.yAxis) {
    normalized.yAxis = {
      type: "category",
      data: [...ys],
      splitArea: { show: true },
    };
  }
  if (!normalized.tooltip) {
    normalized.tooltip = { position: "top" };
  }
  if (!normalized.grid) {
    normalized.grid = { containLabel: true, height: "60%", top: "15%" };
  }
  return normalized;
}

/** ECharts title must be an object; chat agents often send a bare string. */
export function normalizeTitleOption(option: ChartOption): ChartOption {
  if (typeof option.title !== "string") {
    return option;
  }
  return { ...option, title: { text: option.title } };
}

function chartTypeToken(option: ChartOption): string {
  return String(option.chartType || "")
    .toLowerCase()
    .replace(/[_-\s]/g, "");
}

function isIsoDateString(value: unknown): value is string {
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value);
}

/**
 * Calendar-cell object forms used by chat agents / ECharts:
 * `{ date|day|name|label, value }` — not cartesian `{ x, y }` (those stay on axes).
 */
function isDateValuePoint(value: unknown): boolean {
  if (value == null || typeof value !== "object" || Array.isArray(value)) return false;
  const d = value as Record<string, unknown>;
  // Prefer explicit calendar fields; also accept date-like `name`/`label` (common agent mistake).
  // Do NOT treat `{ x: "2026-09-01", y }` as calendar — that is cartesian time series.
  if (Object.prototype.hasOwnProperty.call(d, "x") && Object.prototype.hasOwnProperty.call(d, "y")) {
    return false;
  }
  if (Array.isArray(d.value) && d.value.length >= 2 && isIsoDateString(d.value[0])) {
    return true;
  }
  const date = d.date ?? d.day ?? d.name ?? d.label;
  return isIsoDateString(date);
}

function isDateTuplePoint(value: unknown): boolean {
  return Array.isArray(value) && value.length >= 2 && isIsoDateString(value[0]);
}

export function looksLikeCalendarHeatmapChat(option: ChartOption): boolean {
  const token = chartTypeToken(option);
  if (token === "calendarheatmap" || token === "heatmapcalendar") {
    return true;
  }
  const series = seriesList(option);
  if (series.some((s) => s.type === "heatmap" && (s.coordinateSystem === "calendar" || s.calendarIndex != null))) {
    return true;
  }
  return series.some((s) => {
    const type = String(s.type || "").toLowerCase();
    // Typed line/bar/etc. with date-like x stays cartesian — never force calendar.
    if (type && type !== "heatmap") return false;
    const data = Array.isArray(s.data) ? s.data : [];
    if (data.length === 0) return false;
    const dateObjects = data.every(isDateValuePoint);
    const dateTuples = data.every(isDateTuplePoint);
    return dateObjects || dateTuples;
  });
}

function toCalendarHeatmapCell(value: unknown): [string, number] | null {
  if (isDateTuplePoint(value)) {
    const row = value as unknown[];
    return [String(row[0]).slice(0, 10), Number(row[1])];
  }
  if (!isDateValuePoint(value)) {
    return null;
  }
  const d = value as Record<string, unknown>;
  if (Array.isArray(d.value) && d.value.length >= 2 && isIsoDateString(d.value[0])) {
    return [String(d.value[0]).slice(0, 10), Number(d.value[1])];
  }
  const dateRaw = d.date ?? d.day ?? d.name ?? d.label;
  const date = String(dateRaw).slice(0, 10);
  const num = d.value ?? d.count ?? d.y ?? 0;
  const n = Number(num);
  if (!Number.isFinite(n)) return null;
  return [date, n];
}

/**
 * Chat simplified calendar heatmap:
 * `{ chartType: "calendar-heatmap", series: [{ data: [{ date, value }] }] }`
 * → ECharts calendar + heatmap series.
 */
export function normalizeChatCalendarHeatmapOption(option: ChartOption): ChartOption {
  if (!looksLikeCalendarHeatmapChat(option)) {
    return option;
  }

  const normalized = { ...option } as Record<string, any>;
  delete normalized.chartType;

  const cells: [string, number][] = [];
  normalized.series = seriesList(normalized).map((s) => {
    const data = Array.isArray(s.data) ? s.data : [];
    const converted = data
      .map(toCalendarHeatmapCell)
      .filter((row): row is [string, number] => row != null && Number.isFinite(row[1]));
    cells.push(...converted);
    return {
      name: s.name,
      type: "heatmap",
      coordinateSystem: "calendar",
      calendarIndex: s.calendarIndex ?? 0,
      data: converted,
    };
  });

  const dates = cells.map(([d]) => d).sort();
  const minDate = dates[0];
  const maxDate = dates[dates.length - 1];
  const year = (minDate || new Date().toISOString().slice(0, 10)).slice(0, 4);

  if (!normalized.calendar) {
    normalized.calendar = {
      top: 72,
      left: 50,
      right: 24,
      bottom: 56,
      cellSize: ["auto", 14],
      range: minDate && maxDate ? [minDate, maxDate] : year,
      itemStyle: { borderWidth: 0.5 },
      yearLabel: { show: true },
      dayLabel: { firstDay: 1 },
    };
  }

  delete normalized.xAxis;
  delete normalized.yAxis;
  delete normalized.grid;

  if (!normalized.tooltip) {
    normalized.tooltip = { position: "top" };
  }

  const values = cells.map(([, v]) => v);
  const minVal = values.length ? Math.min(...values) : 0;
  const maxVal = values.length ? Math.max(...values) : 100;
  if (normalized.visualMap == null) {
    normalized.visualMap = {
      min: minVal,
      max: maxVal === minVal ? minVal + 1 : maxVal,
      calculable: true,
      orient: "horizontal",
      left: "center",
      bottom: 8,
      seriesIndex: [0],
      inRange: { color: ["#e8f5e9", "#66bb6a", "#1b5e20"] },
    };
  }

  return normalized;
}

const NON_CARTESIAN_TYPES = new Set([
  "pie",
  "doughnut",
  "gauge",
  "radar",
  "sankey",
  "graph",
  "treemap",
  "sunburst",
  "funnel",
  "heatmap",
  "map",
  "lines",
  "tree",
  "themeRiver",
  "parallel",
  "boxplot",
  "candlestick",
  "liquidFill",
  "wordCloud",
]);

function isCartesianSeriesType(type: unknown): boolean {
  const t = String(type || "line").toLowerCase();
  if (!t) return true;
  if (t.endsWith("3d") || t === "surface") return false;
  if (NON_CARTESIAN_TYPES.has(t)) return false;
  return true;
}

function isXyPoint(value: unknown): value is { x?: unknown; y?: unknown; name?: unknown; value?: unknown } {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

/**
 * Chat agents often emit simplified series points `{ x, y }` without ECharts axes.
 * Convert those into category xAxis + numeric series data, and ensure cartesian axes exist.
 */
export function normalizeCartesianXyOption(option: ChartOption): ChartOption {
  if (looksLikeCalendarHeatmapChat(option) || isCalendarHeatmapChart(option)) {
    return option;
  }

  const series = seriesList(option);
  if (series.length === 0) return option;

  const cartesianSeries = series.filter((s) => isCartesianSeriesType(s.type));
  if (cartesianSeries.length === 0) return option;

  const normalized = { ...option } as Record<string, any>;
  let categories: string[] | null = null;
  let convertedAny = false;

  let scatterConverted = false;

  normalized.series = series.map((s) => {
    if (!isCartesianSeriesType(s.type)) return s;
    const data = Array.isArray(s.data) ? s.data : [];
    if (data.length === 0) return s;

    const xyObjects = data.every(
      (d) => isXyPoint(d) && (Object.prototype.hasOwnProperty.call(d, "x") || Object.prototype.hasOwnProperty.call(d, "y"))
    );
    if (!xyObjects) return s;

    const seriesType = String(s.type || "line").toLowerCase();
    // Scatter needs [x,y] pairs on value axes — do not collapse x into categories.
    if (seriesType === "scatter" || seriesType === "effectscatter") {
      scatterConverted = true;
      return {
        ...s,
        data: data.map((d: any) => [
          d.x != null && d.x !== "" ? Number(d.x) || d.x : 0,
          d.y != null && d.y !== "" ? Number(d.y) : d.value != null ? Number(d.value) : 0,
        ]),
      };
    }

    convertedAny = true;
    const cats = data.map((d: any, i: number) => {
      if (d.x != null) return String(d.x);
      if (d.name != null) return String(d.name);
      return String(i);
    });
    if (categories == null) {
      categories = cats;
    }
    const values = data.map((d: any) => {
      if (d.y != null && d.y !== "") return Number(d.y);
      if (d.value != null && !Array.isArray(d.value)) return Number(d.value);
      return 0;
    });
    return {
      ...s,
      type: s.type || "line",
      data: values,
    };
  });

  if (scatterConverted) {
    if (!normalized.xAxis) normalized.xAxis = { type: "value" };
    if (!normalized.yAxis) normalized.yAxis = { type: "value" };
  }

  if (convertedAny && categories) {
    if (!normalized.xAxis) {
      normalized.xAxis = { type: "category", data: categories, boundaryGap: false };
    } else if (!Array.isArray(normalized.xAxis)) {
      const xa = { ...(normalized.xAxis as Record<string, unknown>) };
      if (!Array.isArray(xa.data) || (xa.data as unknown[]).length === 0) {
        xa.data = categories;
      }
      xa.type = xa.type ?? "category";
      normalized.xAxis = xa;
    }
  }

  // Line/bar/area always need an xAxis; missing axis → ECharts "xAxis 0 not found".
  if (!normalized.xAxis) {
    const firstData = Array.isArray(cartesianSeries[0]?.data) ? (cartesianSeries[0].data as unknown[]) : [];
    const inferredCats = firstData.map((_, i) => String(i));
    normalized.xAxis = {
      type: "category",
      data: inferredCats,
      boundaryGap: String(cartesianSeries[0]?.type || "").toLowerCase() !== "line",
    };
  }
  if (!normalized.yAxis) {
    normalized.yAxis = { type: "value" };
  }
  if (!normalized.tooltip) {
    normalized.tooltip = { trigger: "axis" };
  }
  if (!normalized.grid) {
    normalized.grid = { containLabel: true, left: 48, right: 24, top: 40, bottom: 48 };
  }

  return normalized;
}

export function normalizeChartOption(option: ChartOption): ChartOption {
  let normalized = { ...option };
  normalized = normalizeTitleOption(normalized);
  normalized = normalizeFlatNamedValueAsPieSeries(normalized);
  normalized = normalizeSeriesTypeAliases(normalized);
  normalized = normalizeWaterfallOption(normalized);
  // Tuple pie data before cartesian — otherwise untyped ["name", n] becomes a blank line chart.
  normalized = normalizeTupleNamedValueSeries(normalized);
  normalized = normalizeNamedValueSeries(normalized);
  normalized = normalizeChatCalendarHeatmapOption(normalized);
  normalized = normalizeCartesianXyOption(normalized);
  normalized = normalizeStatisticalCartesianOption(normalized);
  normalized = normalizeCartesianHeatmapAxes(normalized);
  normalized = normalizeCalendarHeatmapOption(normalized);
  normalized = normalize3dOption(normalized);
  normalized = normalizeRadarOption(normalized);
  normalized = normalizeGaugeOption(normalized);
  normalized = normalizeSankeyGraphOption(normalized);
  normalized = normalizeThemeRiverOption(normalized);
  normalized = normalizeParallelOption(normalized);
  return normalized;
}

export function isCartesianHeatmapChart(option: ChartOption): boolean {
  const series = seriesList(option);
  return series.some((s) => s.type === "heatmap" && !s.coordinateSystem);
}

export function isCalendarHeatmapChart(option: ChartOption): boolean {
  const series = seriesList(option);
  return series.some(
    (s) => s.type === "heatmap" && (s.coordinateSystem === "calendar" || s.calendarIndex != null)
  );
}

export function has3dChart(option: ChartOption): boolean {
  return seriesTypes(option).some((t) => t.endsWith("3d") || t === "surface");
}

export function chartLayoutSize(
  option: ChartOption,
  bounds?: { maxHeight?: number; maxWidth?: number }
): { height: number; minWidth: number } {
  let height: number;
  let minWidth: number;
  if (isCalendarHeatmapChart(option)) {
    height = 360;
    minWidth = 720;
  } else if (isCartesianHeatmapChart(option)) {
    height = 420;
    minWidth = 1200;
  } else if (has3dChart(option)) {
    height = 520;
    minWidth = 900;
  } else {
    height = 960;
    minWidth = 840;
  }
  if (bounds?.maxHeight != null) {
    height = Math.min(height, bounds.maxHeight);
  }
  if (bounds?.maxWidth != null) {
    minWidth = Math.min(minWidth, bounds.maxWidth);
  }
  return { height, minWidth };
}
